import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BusyButton, ConfirmDialog, Field, PageHeader, useToast } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { githubAdminApi, githubAdminKeys, type GitHubRegistration } from "./api";
import "../mail-admin/mail-admin.css";

function openGitHub(reg: GitHubRegistration) {
  const form = document.createElement("form");
  form.method = "post";
  form.action = reg.action;
  const manifest = document.createElement("input");
  manifest.type = "hidden";
  manifest.name = "manifest";
  manifest.value = JSON.stringify(reg.manifest);
  form.append(manifest);
  document.body.append(form);
  form.submit();
}

export function GitHubAdminPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery({ queryKey: githubAdminKeys.config, queryFn: githubAdminApi.config });
  const [organization, setOrganization] = useState("");
  const [replaceOpen, setReplaceOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("github");
    if (!result) return;
    if (result === "ready") toast.push("success", "GitHub is ready. Organizations can connect it from AI settings.");
    else toast.push("error", "GitHub could not be registered. Try again.");
    params.delete("github");
    const rest = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
    void qc.invalidateQueries({ queryKey: githubAdminKeys.config });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = useMutation({
    mutationFn: (org: string) => githubAdminApi.register(org.trim()),
    onSuccess: openGitHub,
    onError: (e: Error) => toast.push("error", e.message),
  });

  const data = cfg.data;
  const go = () => start.mutate(organization);

  return (
    <div className="mail-admin">
      <PageHeader title="GitHub" subtitle="Organizations connect their own accounts" />
      {cfg.isLoading || !data ? (
        <Skeleton />
      ) : cfg.isError ? (
        <ErrorState error={cfg.error} retry={() => void cfg.refetch()} />
      ) : (
        <div className="card section">
          <div className="mail-admin-head">
            <div>
              <h2>Connection</h2>
              <p className="muted small">
                Each organization authorizes Backstory from its AI settings and chooses which repositories a draft pull request can use. Register Backstory with GitHub once so that prompt exists.
              </p>
            </div>
            <span className={`badge ${data.configured ? "ok" : "neutral"}`} data-testid="github-app-status">
              {data.configured ? "Ready" : "Not set up"}
            </span>
          </div>
          {data.configured && (
            <p className="muted small" data-testid="github-app-ready">
              Organizations can connect GitHub. Registered as {data.name || data.slug}
              {data.owner ? ` under ${data.owner}` : ""}. {data.url && <a href={data.url}>Open on GitHub</a>}
            </p>
          )}
          <div className="settings-stack">
            <Field label="GitHub organization" hint="Leave this blank to register under the GitHub account you are signed in to. To use an organization, you need to be an owner there.">
              {(f) => (
                <input
                  {...f}
                  className="input"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="Optional"
                  autoComplete="off"
                />
              )}
            </Field>
            {data.configured ? (
              <BusyButton className="btn" busy={start.isPending} busyLabel="Opening GitHub…" onClick={() => setReplaceOpen(true)}>
                Register again
              </BusyButton>
            ) : (
              <BusyButton className="btn primary" busy={start.isPending} busyLabel="Opening GitHub…" onClick={go}>
                Register with GitHub
              </BusyButton>
            )}
          </div>
          <ConfirmDialog
            open={replaceOpen}
            danger
            title="Register again?"
            confirmLabel="Register again"
            onClose={() => setReplaceOpen(false)}
            onConfirm={() => {
              setReplaceOpen(false);
              go();
            }}
          >
            <p>Organizations that already connected GitHub have to connect again.</p>
          </ConfirmDialog>
        </div>
      )}
    </div>
  );
}
